/** Budget · Expenses (eskiz 19): oylik reja/sarf, ro'yxat, "Add expense" oynasi (chek bilan, tasdiqqa ketadi). */
import { rememberPageSize, urlPageSize } from "@/lib/pageSize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useMe } from "@/features/auth/hooks";
import { whenText } from "@/features/finance/money";
import { payError, useArchiveExpense, useBudgetBranches, useCreateExpense, useExpenseSummary, useExpenseTotals, useExpenses, usePayExpense, useUpdateExpense } from "@/features/budget/api";
import { CURRENCIES, money, type Expense, type ExpenseStatus, type ExpenseSummary } from "@/features/budget/types";
import { periodMonth } from "@/features/dashboards/api";
import { ApiError } from "@/lib/api";
import { cn, dayjs, formatDate, formatMoney } from "@/lib/utils";
import { StatusPill } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FilterSelect, Input, NumberInput, Select, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { EmptyState, ErrorState, PageLoading, SimilarNote } from "@/components/ui/misc";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { ContextMenu } from "@/components/ui/context-menu";
import { copyText } from "@/lib/clipboard";
import { expenseMenu } from "@/features/budget/menu";
import { BudgetLayout, useBudgetParams } from "./BudgetLayout";
import { PersonLink } from "@/components/PersonLink";
import { usePersonNav } from "@/features/people/usePersonNav";
import { PayModal, type PayResult } from "@/features/finance/PayModal";
import { categoryRate, type CommissionRates } from "@/features/finance/commission";
import { useAppSettings } from "@/features/drivers/api";

// Matnlar: i18n namespace "budget" (expenses.*)
const EXPENSE_STATUSES: ExpenseStatus[] = ["pending", "approved", "rejected"];
const MAX_RECEIPT_MB = 10;

export function ExpensesPage() {
  const { t } = useTranslation("budget");
  const { period, branch, multiBranch, me, showBranchColumn } = useBudgetParams();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "";
  const status = params.get("status") ?? "";
  const search = params.get("search") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const pageSize = urlPageSize(params.get("page_size"), "expenses");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [archiving, setArchiving] = useState<Expense | null>(null);
  // Tasdiqlangan xarajatni hisobdan to'lash (Pul bo'limiga chiqim)
  const [paying, setPaying] = useState<Expense | null>(null);
  const payExpense = usePayExpense();

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const summary = useExpenseSummary({ period, branch });
  const rates = useAppSettings().data?.commission_rates as CommissionRates | undefined;  // to'lovda komissiya kategoriyadan
  // Sahifalash server'da; jami -- o'sha filtrlar bilan HAMMA sahifa bo'yicha (server), faqat ko'rinib turgani emas
  const filters = { period, branch, category: category || undefined, status: status || undefined, search: search || undefined };
  const expenses = useExpenses({ ...filters, page, page_size: pageSize, ordering: "-date" });
  const sums = useExpenseTotals(filters);
  const rows = expenses.data?.results ?? [];
  const totals = useMemo(() => {
    // Valyuta bo'yicha: USD va UZS bitta songa qo'shilmaydi
    const show = (list: { currency: string; amount: string }[]) => list.map((r) => formatMoney(money(r.amount), r.currency)).join(" · ");
    return { approvedPending: show(sums.data?.approved_pending ?? []) || formatMoney(0), rejected: show(sums.data?.rejected ?? []) };
  }, [sums.data]);
  const archive = useArchiveExpense();
  const navigate = useNavigate();
  const { t: tm } = useTranslation("menu");
  const personNav = usePersonNav();
  const expenseItems = (e: Expense) =>
    expenseMenu(
      e,
      {
        edit: () => setEditing(e),
        archive: () => setArchiving(e),
        navigate: (path) => navigate(path),
        openUrl: (url) => window.open(url, "_blank", "noopener,noreferrer"),
        copy: (text) => void copyText(text),
        profile: personNav,
        pay: () => setPaying(e),
      },
      tm,
    );
  const month = periodMonth(period);
  const hasFilters = !!(category || status || search);

  async function confirmArchive() {
    if (!archiving) return;
    try {
      await archive.mutateAsync(archiving.id);
      toast.success(t("expenses.archived"));
      setArchiving(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("expenses.archiveFailed"));
    }
  }

  return (
    <BudgetLayout
      actions={
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          {t("expenses.add")}
        </Button>
      }
    >
      {summary.data && <SummaryBar summary={summary.data} month={month} />}

      <div className="mt-4 mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={category} onChange={(e) => setParam("category", e.target.value)} className="w-auto min-w-[170px] max-w-[280px]" aria-label={t("expenses.category")}>
          <option value="">{t("expenses.categoryAll")}</option>
          {(summary.data?.categories ?? []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={status} onChange={(e) => setParam("status", e.target.value)} className="w-auto min-w-[150px] max-w-[280px]" aria-label={t("status")}>
          <option value="">{t("expenses.statusAll")}</option>
          {EXPENSE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`common:status.${s}`)}
            </option>
          ))}
        </FilterSelect>
        <Input value={search} onChange={(e) => setParam("search", e.target.value)} placeholder={t("expenses.searchPlaceholder")} className="w-[220px]" aria-label={t("expenses.searchAria")} />
        {hasFilters && (
          <Button variant="link" onClick={() => setParams(new URLSearchParams({ period }), { replace: true })}>
            {t("common:actions.clearFilters")}
          </Button>
        )}
      </div>

      {expenses.isPending ? (
        <PageLoading />
      ) : expenses.isError ? (
        <ErrorState message={expenses.error.message} onRetry={() => expenses.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title={hasFilters ? t("expenses.emptyFiltered") : t("expenses.emptyMonth", { month })} description={t("expenses.emptyDesc")} action={<Button variant="primary" onClick={() => setCreateOpen(true)}>{t("expenses.add")}</Button>} />
      ) : (
        <>
          <SimilarNote show={expenses.data?.fuzzy} />
          <Table>
            <thead>
              <tr>
                <Th>{t("expenses.cols.date")}</Th>
                <Th>{t("expenses.cols.category")}</Th>
                <Th>{t("expenses.cols.description")}</Th>
                {showBranchColumn && <Th>{t("expenses.cols.branch")}</Th>}
                <Th className="text-right">{t("expenses.cols.amount")}</Th>
                <Th>{t("expenses.cols.submittedBy")}</Th>
                <Th>{t("expenses.cols.receipt")}</Th>
                <Th>{t("expenses.cols.status")}</Th>
                <Th>{t("expenses.cols.actions")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <ContextMenu key={e.id} items={() => expenseItems(e)}>
                  <Tr>
                    <Td className="whitespace-nowrap">{formatDate(e.date)}</Td>
                    <Td>{e.category}</Td>
                    <Td className="max-w-[260px] truncate" title={e.description}>
                      {e.description || <span className="text-muted">—</span>}
                    </Td>
                    {showBranchColumn && <Td>{e.branch_name}</Td>}
                    <Td className="text-right font-semibold whitespace-nowrap">{formatMoney(money(e.amount), e.currency)}</Td>
                    <Td>{e.submitted_by ? <PersonLink id={e.submitted_by.id} name={e.submitted_by.display_name} /> : <span className="text-muted">—</span>}</Td>
                    <Td>
                      {e.receipt_url ? (
                        <a href={e.receipt_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                          {e.receipt_name || t("expenses.receipt")}
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </Td>
                    <Td>
                      <StatusPill status={e.status} />
                    </Td>
                    <Td>
                      {e.status === "rejected" ? (
                        <span className="text-[12px] font-semibold text-danger">{t("expenses.reason", { reason: e.rejection_reason || t("expenses.notSpecified") })}</span>
                      ) : e.status === "pending" ? (
                        <span className="flex gap-2 text-[12px]">
                          <button className="text-primary hover:underline" onClick={() => setEditing(e)}>
                            {t("common:actions.edit")}
                          </button>
                          <button className="text-muted hover:text-danger" onClick={() => setArchiving(e)}>
                            {t("common:actions.archive")}
                          </button>
                        </span>
                      ) : e.payment ? (
                        <span className="text-[12px] text-ink-soft" title={whenText(e.payment)}>
                          {t("pay.paidFrom", { account: e.payment.account })}
                        </span>
                      ) : (
                        <span className="flex flex-wrap items-center gap-2 text-[12px]">
                          <button className="font-semibold text-primary hover:underline" onClick={() => setPaying(e)}>
                            {t("pay.payExpense")}
                          </button>
                          <span className="text-muted">{e.decided_by ? t("expenses.by", { name: e.decided_by.display_name }) : ""}</span>
                        </span>
                      )}
                    </Td>
                  </Tr>
                </ContextMenu>
              ))}
            </tbody>
          </Table>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="py-3 text-[12px] text-muted">
              {t("expenses.totalLabel")} <span className="font-semibold text-ink">{totals.approvedPending}</span>
              {totals.rejected && <span>{t("expenses.rejectedSum", { amount: totals.rejected })}</span>}
            </div>
            <Pagination
              page={expenses.data?.page ?? page}
              pages={expenses.data?.pages ?? 1}
              count={expenses.data?.count ?? 0}
              pageSize={pageSize}
              onPage={(p) => setParam("page", String(p))}
              onPageSize={(s) => {
                rememberPageSize("expenses", s);
                setParam("page_size", String(s));
              }} label={t("expenses.paginationLabel")} />
          </div>
        </>
      )}

      <ExpenseModal open={createOpen} onOpenChange={setCreateOpen} period={period} branch={branch} pickBranch={multiBranch} categories={summary.data?.categories ?? []} receiptRequired={summary.data?.receipt_required ?? true} />
      <ExpenseModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} expense={editing} period={period} branch={branch} pickBranch={multiBranch} categories={summary.data?.categories ?? []} receiptRequired={false} />
      <Modal
        open={!!archiving}
        onOpenChange={(o) => !o && setArchiving(null)}
        title={t("expenses.archiveTitle")}
        description={archiving ? `${archiving.category} · ${formatMoney(money(archiving.amount), archiving.currency)} · ${formatDate(archiving.date)}` : undefined}
        size="sm"
        footer={
          <>
            <Button onClick={() => setArchiving(null)}>{t("common:actions.cancel")}</Button>
            <Button variant="danger" loading={archive.isPending} onClick={confirmArchive}>
              {t("common:actions.archive")}
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-soft">{t("expenses.archiveBody")}{me?.role === "owner" ? "" : t("expenses.archiveBodyRequest")}</p>
      </Modal>
      <PayModal
        open={!!paying}
        onOpenChange={(v) => !v && setPaying(null)}
        title={t("pay.expenseTitle")}
        description={paying ? [paying.category, paying.description, paying.branch_name].filter(Boolean).join(" · ") : undefined}
        amounts={paying ? [{ amount: money(paying.amount), currency: paying.currency }] : []}
        branch={paying?.branch}
        commissionPct={paying ? categoryRate(rates, "out", paying.category) : ""}
        confirmLabel={t("pay.pay")}
        loading={payExpense.isPending}
        onConfirm={async (r: PayResult) => {
          if (!paying || r.account === null) return;
          try {
            await payExpense.mutateAsync({ id: paying.id, account: r.account, date: r.date, time: r.time, commission_pct: r.commission_pct });
            toast.success(t("pay.expensePaid"));
            setPaying(null);
          } catch (err) {
            toast.error(payError(err, t, t("common:toast.failed")));
            setPaying(null);
          }
        }}
      />
    </BudgetLayout>
  );
}

function SummaryBar({ summary, month }: { summary: ExpenseSummary; month: string }) {
  const { t } = useTranslation("budget");
  const plan = money(summary.plan);
  const actual = money(summary.actual);
  const pending = money(summary.pending);
  const pct = summary.used_pct;
  const over = pct !== null && pct > 100;
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-card border border-budget bg-budget-soft px-4 py-2.5 text-[13px]">
      <span>
        <span className="font-semibold">{t("expenses.summary.plan", { month, plan: plan > 0 ? formatMoney(plan) : t("expenses.summary.notSet") })}</span> {t("expenses.summary.spent", { amount: formatMoney(actual) })}
        {pct !== null && <span className={cn("ml-1", over && "font-semibold text-danger")}>· {pct}%</span>}
      </span>
      <span className="h-2 w-40 overflow-hidden rounded-full bg-surface/80">
        <span className={cn("block h-full rounded-full", over ? "bg-danger" : "bg-budget")} style={{ width: `${pct === null ? 0 : Math.min(100, pct)}%` }} />
      </span>
      {pending > 0 && <span className="ml-auto text-warning">{t("expenses.summary.waiting", { amount: formatMoney(pending) })}</span>}
    </div>
  );
}

// ---- Qo'shish / tahrirlash oynasi ----

interface ExpenseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expense?: Expense | null;
  period: string;
  branch: number | undefined;
  /** Filial tanlanadi: owner yoki bir nechta filialli menejer */
  pickBranch: boolean;
  categories: string[];
  receiptRequired: boolean;
}

interface FormState {
  date: string;
  category: string;
  amount: string;
  currency: string;
  description: string;
  branch: string;
}

function defaultDate(period: string): string {
  const today = dayjs();
  return today.format("YYYY-MM") === period ? today.format("YYYY-MM-DD") : `${period}-01`;
}

function ExpenseModal({ open, onOpenChange, expense, period, branch, pickBranch, categories, receiptRequired }: ExpenseModalProps) {
  const me = useMe();
  const { t } = useTranslation("budget");
  const editing = !!expense;
  const branches = useBudgetBranches();
  const create = useCreateExpense();
  const update = useUpdateExpense();
  const [form, setForm] = useState<FormState>({ date: defaultDate(period), category: "", amount: "", currency: "USD", description: "", branch: branch ? String(branch) : "" });
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFile(null);
    setForm(
      expense
        ? { date: expense.date, category: expense.category, amount: String(money(expense.amount)), currency: expense.currency, description: expense.description, branch: String(expense.branch) }
        : { date: defaultDate(period), category: categories[0] ?? "", amount: "", currency: "USD", description: "", branch: branch ? String(branch) : "" },
    );
  }, [open, expense, period, branch, categories]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  function pickFile(f: File | null) {
    if (f && f.size > MAX_RECEIPT_MB * 1024 * 1024) {
      setErrors((e) => ({ ...e, receipt: [t("expenses.modal.tooLarge", { mb: MAX_RECEIPT_MB })] }));
      return;
    }
    setErrors((e) => ({ ...e, receipt: [] }));
    setFile(f);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const local: Record<string, string[]> = {};
    if (!form.date) local.date = [t("expenses.modal.dateRequired")];
    if (!form.category) local.category = [t("expenses.modal.chooseCategory")];
    if (!form.amount || Number(form.amount) <= 0) local.amount = [t("expenses.modal.positiveAmount")];
    if (pickBranch && !editing && !form.branch) local.branch = [t("expenses.modal.chooseBranch")];
    if (!editing && receiptRequired && !file) local.receipt = [t("expenses.modal.receiptRequired")];
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }
    setErrors({});
    try {
      if (editing && expense) {
        if (file) {
          const fd = new FormData();
          fd.append("date", form.date);
          fd.append("category", form.category);
          fd.append("amount", form.amount);
          fd.append("currency", form.currency);
          fd.append("description", form.description);
          fd.append("receipt", file);
          fd.append("version", String(expense.version));
          await update.mutateAsync({ id: expense.id, formData: fd });
        } else {
          await update.mutateAsync({ id: expense.id, data: { date: form.date, category: form.category, amount: form.amount, currency: form.currency, description: form.description, version: expense.version } });
        }
        toast.success(t("expenses.modal.updated"));
      } else {
        const fd = new FormData();
        fd.append("date", form.date);
        fd.append("category", form.category);
        fd.append("amount", form.amount);
        fd.append("currency", form.currency);
        fd.append("description", form.description);
        if (pickBranch && form.branch) fd.append("branch", form.branch);
        if (file) fd.append("receipt", file);
        await create.mutateAsync(fd);
        // Owner xarajati tasdiq so'ramaydi (2026-10-18): darhol tasdiqlangan
        if (me.data?.role === "owner") toast.success(t("expenses.modal.added"));
        else toast.success(t("expenses.modal.submitted"), t("expenses.modal.submittedSub"));
      }
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isConflict) {
          toast.error(t("conflict", { who: err.changedBy ?? t("someoneElse") }));
          onOpenChange(false);
          return;
        }
        if (err.fields) setErrors(err.fields);
        else toast.error(err.message);
      } else toast.error(t("expenses.modal.saveFailed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t("expenses.modal.editTitle") : t("expenses.modal.addTitle")}
      size="md"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={(e) => submit(e as unknown as React.FormEvent)}>
            {editing ? t("common:actions.saveChanges") : t("expenses.modal.submit")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("expenses.modal.date")} required error={errors.date} htmlFor="exp-date">
            <Input id="exp-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} aria-invalid={!!errors.date?.length} />
          </Field>
          <Field label={t("expenses.modal.category")} required error={errors.category} htmlFor="exp-category">
            <Select id="exp-category" value={form.category} onChange={(e) => set("category", e.target.value)} aria-invalid={!!errors.category?.length}>
              <option value="">{t("expenses.modal.choose")}</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("expenses.modal.amount")} required error={errors.amount} htmlFor="exp-amount">
            <NumberInput id="exp-amount" min={0} step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="1,285.00" aria-invalid={!!errors.amount?.length} />
          </Field>
          <Field label={t("expenses.modal.currency")} required htmlFor="exp-currency" hint={form.currency === "UZS" ? t("expenses.modal.noConversion") : undefined}>
            <Select id="exp-currency" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          {pickBranch && !editing && (
            <Field label={t("expenses.modal.branch")} required error={errors.branch} htmlFor="exp-branch" className="col-span-2">
              <Select id="exp-branch" value={form.branch} onChange={(e) => set("branch", e.target.value)} aria-invalid={!!errors.branch?.length}>
                <option value="">{t("expenses.modal.choose")}</option>
                {(branches.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <Field label={t("expenses.modal.description")} error={errors.description} htmlFor="exp-description">
          <Textarea id="exp-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("expenses.modal.descPlaceholder")} className="min-h-[60px]" />
        </Field>
        <Field label={t("expenses.modal.receipt")} required={!editing && receiptRequired} error={errors.receipt} hint={editing ? t("expenses.modal.keepReceipt") : undefined}>
          <label
            className={cn(
              "flex cursor-pointer flex-col items-center justify-center rounded-[10px] border border-dashed px-4 py-5 text-center text-[12px]",
              errors.receipt?.length ? "border-danger" : "border-border hover:border-primary",
            )}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickFile(e.dataTransfer.files[0] ?? null);
            }}
          >
            <input type="file" className="sr-only" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
            <span>
              {t("expenses.modal.dropHere")} <span className="text-primary">{t("expenses.modal.browse")}</span>
            </span>
            <span className="mt-0.5 text-muted">{t("expenses.modal.fileTypes", { mb: MAX_RECEIPT_MB })}</span>
          </label>
          {file && (
            <span className="mt-2 inline-flex items-center gap-2 rounded-full bg-fill px-3 py-1 text-[12px]">
              {file.name} · {Math.max(1, Math.round(file.size / 1024))} KB
              <button type="button" className="text-muted hover:text-ink" onClick={() => setFile(null)} aria-label={t("expenses.modal.removeFile")}>
                ×
              </button>
            </span>
          )}
          {editing && !file && expense?.receipt_name && <span className="mt-2 block text-[12px] text-muted">{t("expenses.modal.current", { name: expense.receipt_name })}</span>}
        </Field>
        {!editing && <p className="text-[12px] text-muted">{t("expenses.modal.goesToOwner")}</p>}
      </form>
    </Modal>
  );
}
